/**
 * @file: ControlCenter.jsx
 * @status: Sovereign IAM & Finance System (V12.8.0) - ULTIMATE MONOLITH
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: الربط الشامل بين رادار التمويل وإدارة الهويات؛ إضافة التخصص والعنوان؛ فرض التعتيم الكلي؛ الحفاظ على كافة الدوال بختم @CEO.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ShieldCheck, Users, Lock, Trash2, UserPlus, 
  Edit3, Activity, Search, ArrowRight, MapPin,
  Wallet, Landmark, Award, Cpu, Zap, Globe, TrendingUp, 
  Fingerprint, Rocket, Eye, Briefcase, CheckCircle, Shield,
  RefreshCw, Database, Terminal, FileText, LayoutGrid, Info
} from 'lucide-react';
import { message, Modal, Input, Select, Tag, Progress, Badge, Table, Tooltip, Row, Col, Divider } from 'antd';

import logo2026 from '../assets/logo2026.png'; 

const { Option } = Select;

const ControlCenter = () => {
  const navigate = useNavigate();
  
  // --- [ 🔐 إعدادات الجسر السحابي السيادي ] ---
  const API_URL = "https://portal.huger.sa/api.php";
  const CEO_PIN = "2026"; 

  // --- [ 🛡️ حالات النظام والسيادة ] ---
  const [userRole, setUserRole] = useState(localStorage.getItem('huger_user_role'));
  const [userName, setUserName] = useState(localStorage.getItem('huger_user_name'));
  const [isAuthorized, setIsAuthorized] = useState(() => localStorage.getItem('huger_authenticated') === 'true');
  const [pinInput, setPinInput] = useState("");
  const [users, setUsers] = useState([]);
  const [financingRequests, setFinancingRequests] = useState([]); 
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // --- [ 📊 مصفوفة المقاييس السيادية ] ---
  const [sovereignMetrics] = useState({
    industrial_loan: 24280000,
    loan_status: "Awaiting Strategic Partner", 
    partner_solvency: 75, 
    intellectual_assets: 3124, 
    localization_rate: "92%",
    last_financial_sync: new Date().toLocaleDateString()
  });

  // --- [ 📝 بروتوكول بيانات الهوية المطور ] ---
  const [userData, setUserData] = useState({
    name: '', 
    role: 'Staff', 
    specialization: '', 
    address: '', 
    pin: '', 
    access_level: 'Staff'
  });

  // --- [ 📡 الدالة (1): جلب رادار التمويل ] ---
  const fetchFinancingData = useCallback(async () => {
    try {
      const response = await axios.post(API_URL, { action: 'f_radar' });
      if (Array.isArray(response.data)) {
        setFinancingRequests(response.data);
      }
    } catch (error) {
      console.error("Critical: Finance Radar Link Severed");
    }
  }, [API_URL]);

  // --- [ 📡 الدالة (2): جلب الهويات من مجمع البيانات ] ---
  const fetchIdentities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(API_URL, { action: 'get_identities' });
      const rawData = Array.isArray(res.data) ? res.data : [];
      setUsers(rawData.map(u => ({ 
        ...u, 
        status: u.status || 'Active',
        specialization: u.specialization || 'Technical Expert',
        address: u.address || 'Taif Industrial Area'
      })));
    } catch (err) {
      message.error("عذراً رئيس، تعذر الاتصال بالجسر السحابي.");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // --- [ ⏳ مزامنة البيانات عند التفويض ] ---
  useEffect(() => {
    if (isAuthorized) { 
      fetchIdentities();
      fetchFinancingData(); 
    }
  }, [isAuthorized, fetchIdentities, fetchFinancingData]);

  // --- [ 🏦 الدالة (3): بروتوكول مزامنة التمويل السيادي ] ---
  const handleFinancingSync = async (schoolId, amount, platform) => {
      const hide = message.loading(`جاري الربط مع منصة ${platform}...`, 0);
      try {
          const response = await axios.post(API_URL, {
              action: 'submit_financing',
              school_id: schoolId,
              total_amount: amount,
              platform_name: platform,
              sealed_by: "@CEO"
          });
          if (response.data.success) {
              message.success(`تم تعميد طلب التمويل بنجاح ✅`);
              fetchFinancingData(); 
          }
      } catch (error) {
          message.error("عائق في الاتصال بالجسر المالي");
      } finally {
          hide();
      }
  };

  // --- [ ✍️ الدالة (4): حفظ وتحديث المفوضين بختم @CEO ] ---
  const handleSaveUser = async () => {
    if (userRole !== 'CEO') return message.error("صلاحية محصورة للرئيس @CEO.");
    
    const payload = editingUser ? editingUser : userData;
    
    if (!payload.name || !payload.pin || !payload.specialization || !payload.address) {
        return message.warning("سعادة الرئيس، يرجى إكمال كافة البيانات (الاسم، التخصص، العنوان، PIN).");
    }

    const hide = message.loading("جاري تشفير البيانات والمزامنة السحابية...", 0);
    setSyncing(true);

    try {
      const action = editingUser ? 'update_identity' : 'add_identity';
      const response = await axios.post(API_URL, { 
          ...payload, 
          action,
          role: payload.access_level, 
          authorized_by: "@CEO",
          timestamp: new Date().toISOString()
      });
      
      if (response.data && response.data.success) {
        message.success(editingUser ? "تم تحديث البيانات السيادية ✅" : "تم تعميد الهوية الجديدة بنجاح ✅");
        setIsModalVisible(false);
        setEditingUser(null);
        setUserData({ name: '', role: 'Staff', specialization: '', address: '', pin: '', access_level: 'Staff' });
        fetchIdentities();
      } else {
        message.error(response.data.message || "فشل في تعميد البيانات.");
      }
    } catch (err) { 
      message.error("فشل الجسر السحابي في تنفيذ المهمة التقنية."); 
    } finally { 
      hide(); 
      setSyncing(false);
    }
  };

  // --- [ 🗑️ الدالة (5): تطهير سجل المفوضين ] ---
  const deleteUser = async (id) => {
    if (userRole !== 'CEO') return;
    
    Modal.confirm({
      title: 'تأكيد التطهير السيادي',
      content: 'هل تود حذف هذه الهوية نهائياً من مجمع البيانات السحابي؟ هذا الإجراء لا يمكن التراجع عنه.',
      okText: 'تطهير السجل',
      okDanger: true,
      cancelText: 'إلغاء',
      centered: true,
      onOk: async () => {
        try {
          const res = await axios.post(API_URL, { action: 'delete_identity', id });
          if (res.data.success) {
            setUsers(prev => prev.filter(u => u.id !== id));
            message.success("تم التطهير بنجاح ✅");
          }
        } catch (err) { message.error("فشل التطهير."); }
      }
    });
  };

  // --- [ 🔓 الدالة (6): بروتوكول العبور ] ---
  const handleAccess = async () => {
    if (pinInput === CEO_PIN) {
      localStorage.setItem('huger_authenticated', 'true');
      localStorage.setItem('huger_user_role', 'CEO');
      localStorage.setItem('huger_user_name', 'سعادة الرئيس عبدالله');
      setIsAuthorized(true); setUserRole('CEO'); setUserName('سعادة الرئيس عبدالله');
      message.success("مرحباً بك يا سعادة الرئيس عبدالله @CEO");
      return;
    }
    
    try {
      const res = await axios.post(API_URL, { action: 'verify_pin', pin: pinInput });
      if (res.data && res.data.success) {
        localStorage.setItem('huger_authenticated', 'true');
        localStorage.setItem('huger_user_role', res.data.access_level);
        localStorage.setItem('huger_user_name', res.data.name);
        setIsAuthorized(true); setUserRole(res.data.access_level); setUserName(res.data.name);
        message.success(`تم منح النفاذ: ${res.data.name}`);
      } else { 
        message.error(res.data.message || "الرمز السيادي غير صحيح."); 
      }
    } catch (err) { 
      message.error("عطل في بوابة العبور السحابية."); 
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthorized(false);
    navigate('/');
  };

  // --- [ 🌐 الواجهة (A): بوابة العبور السيادية ] ---
  if (!isAuthorized) {
    return (
      <div className="h-screen flex flex-col md:flex-row overflow-hidden bg-[#020617]" dir="rtl">
        <div className="hidden md:flex md:w-1/2 relative flex-col justify-center p-12 border-l border-white/5">
          <div className="absolute inset-0 z-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1586724230413-cd69a537496b?q=80&w=2070&auto=format&fit=crop')`, filter: 'grayscale(100%)' }}></div>
          <div className="absolute inset-0 bg-gradient-to-l from-[#020617] via-transparent to-transparent z-10"></div>
          <div className="relative z-20 space-y-6">
            <div className="flex gap-4 opacity-50 scale-90 origin-right">
               <img src="https://upload.wikimedia.org/wikipedia/ar/archive/a/a2/20211115131003%21Saudi_Vision_2030_logo.svg" alt="Vision 2030" className="h-10 brightness-0 invert" />
               <img src="https://upload.wikimedia.org/wikipedia/ar/b/be/Ministry_of_Education_Saudi.svg" alt="MOE" className="h-10 brightness-0 invert" />
            </div>
            <h2 className="text-6xl font-black text-white leading-tight italic uppercase tracking-tighter">ضخامة <span className="text-[#F57C00]">HUGER</span> <br /><span className="text-2xl text-slate-400">للصناعات التعليمية المتخصصة</span></h2>
            <p className="text-base text-slate-400 max-w-sm font-medium opacity-80 italic border-r-2 border-[#F57C00] pr-6">توطين صناعة الأدوات التفاعلية ومنهجيات STEAM العالمية بختم @CEO.</p>
            <div className="grid grid-cols-2 gap-6 pt-6">
              <div className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-md">
                <h4 className="text-[#F57C00] font-black text-2xl tracking-tighter">{sovereignMetrics.intellectual_assets}+</h4>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">براءة اختراع وأصل فكري</p>
              </div>
              <div className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-md">
                <h4 className="text-emerald-500 font-black text-2xl tracking-tighter">{sovereignMetrics.localization_rate}</h4>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">معدل التوطين الصناعي</p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-6 z-20">
          <div className="w-full max-w-xs">
            <div className="flex justify-center mb-8">
              <div className="bg-white/5 p-5 rounded-[2.5rem] border border-white/10 shadow-2xl">
                <img src={logo2026} alt="HUGER" className="h-12 w-auto animate-pulse" />
              </div>
            </div>
            <h1 className="text-3xl font-black text-white text-center mb-1 italic uppercase tracking-tighter">SOVEREIGN <span className="text-[#F57C00]">GATE</span></h1>
            <p className="text-slate-500 text-[10px] font-black mb-8 text-center uppercase tracking-[0.3em] opacity-60">مركز التحكم بالهوية والأصول لضخامة</p>
            <div className="bg-white/[0.02] backdrop-blur-2xl p-8 rounded-[3rem] border border-white/10 shadow-2xl">
              <input id="ceo_pin_access" type="password" placeholder="الرمز السيادي" className="w-full bg-black/60 border border-white/10 p-5 rounded-2xl text-center text-2xl text-white outline-none focus:border-[#F57C00]/50 tracking-[0.5em] mb-6 transition-all" value={pinInput} onChange={(e) => setPinInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAccess()} />
              <button onClick={handleAccess} className="w-full bg-gradient-to-r from-[#F57C00] to-[#E65100] py-5 rounded-2xl font-black text-white flex items-center justify-center gap-3 hover:scale-[1.02] transition-all text-sm shadow-xl shadow-orange-500/20 border-none cursor-pointer">تأكيد الهوية <ShieldCheck size={20} /></button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- [ 🌐 الواجهة (B): غرفة العمليات المركزية ] ---
  return (
    <div className="min-h-screen bg-[#050B14] text-slate-200 p-4 lg:p-8 relative overflow-hidden" dir="rtl">
      
      {/* هيدر الصفحة المكتمل */}
      <header className="max-w-7xl mx-auto flex justify-between items-center mb-12 border-b border-white/5 pb-8 relative z-10">
        <div className="flex items-center gap-6">
          <div className="p-1 bg-white/5 rounded-2xl border border-white/10 shadow-inner">
            <img src={logo2026} alt="HUGER" className="h-14 w-auto" />
          </div>
          <div>
            <h1 className="text-3xl font-black italic text-[#F57C00] uppercase tracking-tighter leading-none">مركز التحكم السيادي</h1>
            <div className="flex items-center gap-3 mt-2">
                <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest opacity-60">HUGER Digital HQ • Taif 2026</span>
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Tag color="orange" className="font-black px-6 py-1 rounded-full uppercase text-[11px] border-none bg-orange-500/10 text-orange-500 italic">@{userRole}: {userName}</Tag>
          <button onClick={handleLogout} className="bg-red-500/10 text-red-500 px-6 py-2.5 rounded-2xl font-black text-[10px] hover:bg-red-500 hover:text-white transition-all border-none cursor-pointer">خروج آمن</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto space-y-10 relative z-10">
        
        {/* 1. قسم رادار التمويل والسيولة (نظام مراقبة القرض الصناعي) */}
        <section className="bg-gradient-to-br from-[#0A192F] to-[#050B14] border border-[#F57C00]/20 rounded-[3.5rem] p-10 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 p-10 opacity-5 group-hover:opacity-10 transition-opacity"><Landmark size={150} className="text-white" /></div>
          
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} lg={16}>
              <div className="relative z-10">
                <h3 className="text-2xl font-black text-white flex items-center gap-4 italic uppercase mb-2">
                   <Wallet className="text-[#F57C00]" size={32} /> رادار التمويل والسيولة
                </h3>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-8">مراقبة تدفقات القرض الصناعي (24.28 مليون) والتحصيل بختم @CEO</p>
                
                <div className="max-h-[250px] overflow-y-auto pr-4 custom-scrollbar">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {financingRequests.length > 0 ? financingRequests.map((req) => (
                      <div key={req.id} className="flex justify-between items-center bg-white/[0.03] p-5 rounded-2xl border border-white/5 hover:border-[#F57C00]/30 transition-all group/item shadow-lg">
                        <div className="text-right">
                          <span className="text-[13px] font-black text-white italic group-hover/item:text-[#F57C00] transition-colors">{req.school_name}</span>
                          <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px] text-slate-500 uppercase font-bold tracking-tight">{req.platform_name}</span>
                              <span className="text-slate-700 font-bold">|</span>
                              <span className="text-[11px] text-emerald-500 font-black tracking-tighter">{parseFloat(req.total_amount).toLocaleString()} SAR</span>
                          </div>
                        </div>
                        <Tag color={req.status === 'Approved' ? 'green' : 'orange'} className="font-black text-[9px] px-5 py-1 rounded-full border-none shadow-inner">{req.status}</Tag>
                      </div>
                    )) : <div className="col-span-2 py-12 text-center text-slate-700 italic border-2 border-dashed border-white/5 rounded-3xl">لا توجد سجلات تمويل نشطة حالياً</div>}
                  </div>
                </div>
              </div>
            </Col>
            
            <Col xs={24} lg={8}>
              <div className="bg-black/40 p-8 rounded-[2.5rem] border border-white/5 text-center relative z-10 shadow-inner">
                <TrendingUp className="text-emerald-500 mx-auto mb-4" size={40} />
                <h4 className="text-slate-500 font-black text-[10px] uppercase tracking-widest mb-2">إجمالي السيولة المعتمدة</h4>
                <div className="text-3xl font-black text-white italic tracking-tighter leading-none mb-4">24,280,000 <span className="text-xs text-[#F57C00]">SAR</span></div>
                <Progress percent={sovereignMetrics.partner_solvency} strokeColor="#10b981" trailColor="rgba(255,255,255,0.05)" strokeWidth={10} className="mb-4" />
                <p className="text-[9px] text-slate-600 font-bold uppercase italic tracking-tighter">Sync Status: Fully Operational</p>
              </div>
            </Col>
          </Row>
        </section>

        {/* 2. قسم إدارة المفوضين والكوادر السياديين */}
        {userRole === 'CEO' && (
          <section className="bg-[#0A192F]/40 border border-white/5 rounded-[3.5rem] p-10 backdrop-blur-3xl shadow-2xl relative">
            <div className="flex justify-between items-center mb-10">
              <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20"><Users className="text-blue-500" size={30} /></div>
                  <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">إدارة المفوضين والكوادر السياديين</h2>
              </div>
              <button onClick={() => { setEditingUser(null); setIsModalVisible(true); }} className="bg-blue-600 hover:bg-blue-500 text-white px-10 py-4 rounded-2xl font-black text-[12px] shadow-2xl shadow-blue-600/20 transition-all hover:scale-105 flex items-center gap-3 border-none cursor-pointer active:scale-95">
                تدشين مفوض جديد <UserPlus size={20} />
              </button>
            </div>
            
            <div className="bg-black/40 rounded-[2.5rem] overflow-hidden border border-white/5 shadow-inner">
              <div className="max-h-[450px] overflow-y-auto custom-scrollbar px-6">
                <table className="w-full text-right" dir="rtl">
                  <thead className="bg-white/[0.01] sticky top-0 z-10 text-[11px] font-black uppercase text-slate-500 tracking-[0.2em]">
                    <tr>
                      <th className="p-6">المفوض الرسمي</th>
                      <th className="p-6">التخصص المهني</th>
                      <th className="p-6">العنوان والمنطقة</th>
                      <th className="p-6">الرتبة</th>
                      <th className="p-6 text-center">الإجراء</th>
                    </tr>
                  </thead>
                  <tbody className="text-[13px]">
                    {users.map((user, idx) => (
                      <tr key={user.id || idx} className="border-b border-white/5 hover:bg-white/[0.02] transition-all duration-300 group">
                        <td className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-slate-800 to-slate-900 border border-white/10 flex items-center justify-center font-black text-white text-sm shadow-lg group-hover:border-[#F57C00]/40 transition-all">{user.name ? user.name[0] : '?'}</div>
                                <div>
                                    <div className="font-black text-white italic group-hover:text-[#F57C00] transition-colors">{user.name}</div>
                                    <div className="text-[8px] text-slate-600 uppercase font-bold mt-1 tracking-widest">ID: {2026 + (user.id || idx)}</div>
                                </div>
                            </div>
                        </td>
                        <td className="p-6"><span className="text-slate-400 font-bold italic flex items-center gap-2"><Briefcase size={14} className="text-blue-500/50" /> {user.specialization}</span></td>
                        <td className="p-6"><span className="text-slate-500 text-xs font-medium flex items-center gap-2"><MapPin size={14} className="text-red-500/40" /> {user.address}</span></td>
                        <td className="p-6"><Tag className="font-black text-[9px] px-4 py-0.5 rounded-lg border-none bg-blue-500/10 text-blue-400 uppercase italic">{user.role}</Tag></td>
                        <td className="p-6 flex justify-center gap-6">
                          <Tooltip title="تعديل السجل"><Edit3 size={20} className="cursor-pointer text-slate-600 hover:text-blue-400 transition-all hover:scale-110" onClick={() => { setEditingUser(user); setIsModalVisible(true); }} /></Tooltip>
                          <Tooltip title="تطهير الهوية"><Trash2 size={20} className="cursor-pointer text-slate-600 hover:text-red-500 transition-all hover:scale-110" onClick={() => deleteUser(user.id)} /></Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* ⚠️ المودال السيادي المحصن V12.8.0 - علاج البياض المطلق ⚠️ */}
      <Modal 
        title={
            <div className="modal-title-sovereign">
                <Shield className="text-[#F57C00]" size={24} /> 
                <span className="mr-3">Induction Hub - تعميد هوية رقمية</span>
            </div>
        }
        open={isModalVisible} 
        onOk={handleSaveUser} 
        onCancel={() => { setIsModalVisible(false); setEditingUser(null); }} 
        centered 
        footer={null}
        width={650}
        getContainer={false} // 👈 إجبار المودال على البقاء داخل نطاق الـ DOM الداكن
        className="huger-sovereign-modal"
      >
        <div className="space-y-6 py-6" dir="rtl">
          {/* 1. حقل الاسم الكامل */}
          <div className="space-y-2">
            <label className="sovereign-field-label">الاسم الكامل للمفوض (بالسجلات الرسمية)</label>
            <Input id="f_full_name" prefix={<ShieldCheck size={20} className="text-slate-500 ml-2" />} placeholder="الاسم الرباعي الكامل" className="huger-input-v-final" value={editingUser?.name || userData.name} onChange={(e) => editingUser ? setEditingUser({...editingUser, name: e.target.value}) : setUserData({...userData, name: e.target.value})} />
          </div>

          {/* 2. حقول التخصص والرتبة */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="sovereign-field-label">التخصص المهني</label>
              <Input id="f_spec" prefix={<Briefcase size={20} className="text-slate-500 ml-2" />} placeholder="مثال: خبير تقنيات STEAM" className="huger-input-v-final" value={editingUser?.specialization || userData.specialization} onChange={(e) => editingUser ? setEditingUser({...editingUser, specialization: e.target.value}) : setUserData({...userData, specialization: e.target.value})} />
            </div>
            <div className="space-y-2">
              <label className="sovereign-field-label">الرتبة السيادية</label>
              <Select id="f_access" className="w-full huger-select-v-final" value={editingUser?.access_level || userData.access_level} onChange={(val) => editingUser ? setEditingUser({...editingUser, access_level: val, role: val}) : setUserData({...userData, access_level: val, role: val})} dropdownClassName="huger-dropdown-v-final">
                <Option value="CEO">CEO - رئيس تنفيذي</Option>
                <Option value="Manager">Manager - مدير قطاع</Option>
                <Option value="Staff">Staff - كادر فني</Option>
                <Option value="Investor">Investor - شريك استراتيجي</Option>
              </Select>
            </div>
          </div>

          {/* 3. حقل العنوان الوطني */}
          <div className="space-y-2">
            <label className="sovereign-field-label">العنوان الوطني / موقع العمل الميداني</label>
            <Input id="f_address" prefix={<MapPin size={20} className="text-slate-500 ml-2" />} placeholder="المدينة - المنطقة - الرمز البريدي" className="huger-input-v-final" value={editingUser?.address || userData.address} onChange={(e) => editingUser ? setEditingUser({...editingUser, address: e.target.value}) : setUserData({...userData, address: e.target.value})} />
          </div>

          {/* 4. حقل الـ PIN السيادي */}
          <div className="space-y-2">
            <label className="sovereign-field-label">رمز العبور السيادي (4 خانات مشفرة)</label>
            <Input.Password id="f_pin" prefix={<Lock size={20} className="text-slate-500 ml-2" />} placeholder="****" maxLength={4} className="huger-input-v-final text-center text-2xl tracking-[1em]" value={editingUser?.pin || userData.pin} onChange={(e) => editingUser ? setEditingUser({...editingUser, pin: e.target.value}) : setUserData({...userData, pin: e.target.value})} />
          </div>

          {/* زر التنفيذ */}
          <button id="btn_seal_final" onClick={handleSaveUser} disabled={syncing} className="huger-btn-seal-ultimate">
             {syncing ? <RefreshCw className="animate-spin" /> : <CheckCircle size={26} />} 
             <span className="mr-3">{editingUser ? 'تحديث البيانات بختم @CEO' : 'اعتماد الهوية في السجل السيادي'}</span>
          </button>
        </div>

        {/* 🛡️ بروتوكول التعتيم النهائي المطلق 🛡️ */}
        <style>{`
          .huger-sovereign-modal .ant-modal-content { 
              background-color: #050B14 !important; 
              border: 1px solid rgba(245,124,0,0.4) !important; 
              border-radius: 3rem !important; 
              box-shadow: 0 0 100px rgba(0,0,0,1) !important;
              padding: 20px !important;
          }
          .huger-sovereign-modal .ant-modal-header { 
              background: #050B14 !important; 
              border-bottom: 1px solid rgba(255,255,255,0.05) !important; 
              padding: 20px 30px !important; 
          }
          .modal-title-sovereign { color: white !important; font-weight: 900 !important; display: flex !important; align-items: center !important; font-style: italic !important; }
          .huger-sovereign-modal .ant-modal-close { color: #F57C00 !important; top: 30px !important; }
          
          .sovereign-field-label { color: #F57C00 !important; font-size: 10px !important; font-weight: 900 !important; text-transform: uppercase !important; letter-spacing: 2px !important; margin-right: 10px !important; }
          
          .huger-input-v-final { 
              background: #020617 !important; 
              border: 1px solid #1e293b !important; 
              border-radius: 1.2rem !important; 
              padding: 15px !important; 
              color: white !important; 
              transition: all 0.3s ease !important;
          }
          .huger-input-v-final:hover, .huger-input-v-final:focus-within { border-color: #F57C00 !important; background: rgba(245,124,0,0.05) !important; }
          .huger-input-v-final input { color: white !important; background: transparent !important; font-weight: 700 !important; font-size: 14px !important; }
          .huger-input-v-final input::placeholder { color: #334155 !important; }
          
          .huger-select-v-final .ant-select-selector { 
              background: #020617 !important; 
              border: 1px solid #1e293b !important; 
              color: white !important; 
              height: 55px !important; 
              display: flex !important; 
              align-items: center !important; 
              border-radius: 1.2rem !important; 
              font-weight: 700 !important;
          }
          .huger-dropdown-v-final { background-color: #020617 !important; border: 1px solid #F57C00 !important; border-radius: 1rem !important; overflow: hidden !important; }
          .ant-select-item { color: #94a3b8 !important; padding: 12px 20px !important; }
          .ant-select-item-option-selected { background: #F57C00 !important; color: white !important; font-weight: 900 !important; }
          .ant-select-item-option-hover { background: rgba(245,124,0,0.1) !important; }

          .huger-btn-seal-ultimate { 
              width: 100% !important; 
              margin-top: 30px !important; 
              padding: 22px !important; 
              background: #F57C00 !important; 
              color: white !important; 
              font-weight: 900 !important; 
              border-radius: 2rem !important; 
              border: none !important; 
              cursor: pointer !important; 
              box-shadow: 0 15px 40px rgba(245,124,0,0.4) !important; 
              display: flex !important; 
              justify-content: center !important; 
              align-items: center !important; 
              font-size: 16px !important; 
              font-style: italic !important;
              transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
          }
          .huger-btn-seal-ultimate:hover { background: #ff9800 !important; transform: translateY(-5px) !important; box-shadow: 0 20px 50px rgba(245,124,0,0.5) !important; }
          .huger-btn-seal-ultimate:active { transform: scale(0.98) !important; }

          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(245,124,0,0.4); border-radius: 10px; }
        `}</style>
      </Modal>

      <footer className="mt-20 py-10 text-center border-t border-white/5 opacity-20">
        <div className="flex justify-center gap-10 mb-6 text-slate-500"><Database size={24}/><ShieldCheck size={24}/><Globe size={24}/><Terminal size={24}/></div>
        <p className="text-gray-800 text-[11px] font-black uppercase tracking-[1.5em] italic leading-loose">HUGER SOVEREIGN ECOSYSTEM • 2026 • AUTHORIZED BY @CEO</p>
      </footer>
    </div>
  );
};

export default ControlCenter;